/**
 * Owns image-manager URL filters, segment tabs, and the two empty states.
 * Group data, selection, and network loading remain page-owned callbacks.
 */
export function createImageManagerFilterController({
  getListingId,
  setListingId,
  getUserRole,
  refreshGroups,
  resetGroupUi,
  documentRef = document,
  locationRef = globalThis.location,
  historyRef = globalThis.history,
  now = () => Date.now(),
} = {}) {
  let segment = 'linked';

  function currentUrl() {
    return new URL(locationRef.href);
  }

  function resolveListingFilter() {
    const urlId = new URLSearchParams(locationRef.search).get('listingId');
    if (urlId) {
      const id = parseInt(urlId, 10);
      setListingId(id);
      return id;
    }
    return getListingId();
  }

  function resolveSegment() {
    const tab = new URLSearchParams(locationRef.search).get('tab');
    return tab === 'standalone' || tab === 'linked' ? tab : 'linked';
  }

  function getSegment() {
    return segment;
  }

  function syncSegmentTabUi() {
    documentRef.querySelectorAll('.im-segment-tab').forEach((button) => {
      const active = button.dataset.segment === segment;
      button.classList.toggle('active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  function clearListingIdFromUrl() {
    const url = currentUrl();
    if (!url.searchParams.has('listingId')) return;
    url.searchParams.delete('listingId');
    historyRef.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  function createClearFilterLink() {
    const link = documentRef.createElement('a');
    link.href = '#';
    link.textContent = '显示全部';
    link.addEventListener('click', (event) => {
      event.preventDefault();
      setListingId(null);
      clearListingIdFromUrl();
      refreshGroups();
    });
    return link;
  }

  function updateEmptyStateCopy() {
    const empty = documentRef.getElementById('im-images-empty');
    if (!empty) return;
    const title = empty.querySelector('.im-empty-title');
    const hint = empty.querySelector('.im-empty-hint');
    const listingId = resolveListingFilter();
    if (listingId) {
      if (title) title.textContent = '该 Listing 下暂无图片';
      if (hint) hint.replaceChildren('可切换上方分类查看其他图片，或 ', createClearFilterLink());
      return;
    }
    if (segment === 'standalone') {
      if (title) title.textContent = '暂无仅生图项目';
      if (hint) {
        const imageSuite = documentRef.createElement('a');
        imageSuite.href = '/image-suite.html';
        imageSuite.textContent = '套图生成';
        hint.replaceChildren('在 ', imageSuite, ' 创建项目并保存图片后，会显示在这里');
      }
      return;
    }
    if (title) title.textContent = '暂无已保存的图片';
    if (hint) hint.textContent = '在 Listing 生成流程中生成并保存图片后，会在这里显示';
  }

  function renderEmptySubtitle(subtitle) {
    if (!subtitle) return;
    const listingId = resolveListingFilter();
    if (listingId) {
      subtitle.replaceChildren(`Listing #${listingId} 下暂无图片 · `, createClearFilterLink());
      return;
    }
    subtitle.textContent = segment === 'standalone' ? '仅生图项目 · 暂无图片' : '关联 Listing · 暂无图片';
  }

  function setSegment(nextSegment) {
    segment = nextSegment === 'standalone' ? 'standalone' : 'linked';
    const url = currentUrl();
    url.searchParams.set('tab', segment);
    historyRef.replaceState(null, '', url.pathname + url.search + url.hash);
    syncSegmentTabUi();
    updateEmptyStateCopy();
  }

  function hydrateSegment() {
    segment = resolveSegment();
    syncSegmentTabUi();
    updateEmptyStateCopy();
    return segment;
  }

  function bindSegmentTabs() {
    documentRef.querySelectorAll('.im-segment-tab').forEach((button) => {
      if (button.dataset.imBound === '1') return;
      button.dataset.imBound = '1';
      button.addEventListener('click', () => {
        const nextSegment = button.dataset.segment;
        if (!nextSegment || nextSegment === segment) return;
        setSegment(nextSegment);
        setListingId(null);
        clearListingIdFromUrl();
        resetGroupUi();
        refreshGroups();
      });
    });
  }

  function buildGroupedApiUrl() {
    const listingId = resolveListingFilter();
    const params = new URLSearchParams();
    params.set('_t', String(now()));
    if (!listingId) params.set('segment', segment);
    if (listingId) {
      params.set('listingId', String(listingId));
    } else {
      const role = getUserRole();
      if (role === 'admin' || role === 'leader') params.set('scope', 'all');
    }
    return `/api/image/assets/grouped?${params.toString()}`;
  }

  return Object.freeze({
    resolveListingFilter,
    getSegment,
    clearListingIdFromUrl,
    updateEmptyStateCopy,
    renderEmptySubtitle,
    hydrateSegment,
    bindSegmentTabs,
    buildGroupedApiUrl,
  });
}
